// Event colour resolution (TOKENS.md §3, §5). Presentation, not data — it lives
// here and reads the theme, never the data hook.
//
// AA fix (adopted): the universal "ink-on-tint" treatment passes WCAG 2.1 AA —
// `alpha(base, 0.13)` fill + a colored left border + `text.primary` ink —
// instead of white-on-light-fill (which fails AA at 2.15–2.77:1). Solid fill +
// white ink is reserved for the two genuinely dark sources (`pestpac`,
// `next_action_date`), which already pass AA against white.

import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import { CALENDAR_COLOR_BASES } from '@sales-center/common'
import type { CalendarColorKey, CalendarEvent } from './typings'

export type ResolvedEventColor = {
  /** Tile / chip background. */
  fill: string
  /** Text colour that passes AA against `fill`. */
  ink: string
  /** Left-border / accent colour (the base hue). */
  border: string
}

// Genuinely dark sources — solid fill + white ink (both pass AA against white).
const SOLID_INK_KEYS: ReadonlySet<CalendarColorKey> = new Set<CalendarColorKey>(
  ['pestpac', 'next_action_date'],
)

/**
 * The semantic colour key for an event. Sub-type overrides source; an
 * unrecognised source resolves to `unknown` (grey), never a crash.
 */
export function resolveColorKey(event: CalendarEvent): CalendarColorKey {
  if (event.source === 'sales_center') {
    return event.subType ?? 'sales_center'
  }
  if (event.source === 'pestpac') {
    return 'pestpac'
  }
  if (event.source === 'google') {
    return 'google'
  }
  return 'unknown'
}

// The theme is the source of truth; `CALENDAR_COLOR_BASES` is the same object the
// theme is built from, reused here only for the defensive theme-unresolved case
// so a colour never fails invisibly to `undefined` — no second copy to drift.
function baseColor(theme: Theme, key: CalendarColorKey): string {
  return theme.palette.calendarColors?.[key] ?? CALENDAR_COLOR_BASES[key]
}

/**
 * The AA-safe colour treatment for a semantic key. Dark sources get a solid
 * fill + white ink; every other (lighter) source gets the ink-on-tint treatment
 * so white-on-light — which fails AA (e.g. google amber at ~1.9:1) — never
 * ships. Used by both event tiles and the filter chips so the two stay in sync.
 */
export function resolveColorByKey(
  theme: Theme,
  key: CalendarColorKey,
): ResolvedEventColor {
  const base = baseColor(theme, key)

  if (SOLID_INK_KEYS.has(key)) {
    return { fill: base, ink: theme.palette.common.white, border: base }
  }

  return {
    fill: alpha(base, 0.13),
    ink: theme.palette.text.primary,
    border: base,
  }
}

export function resolveEventColor(
  event: CalendarEvent,
  theme: Theme,
): ResolvedEventColor {
  return resolveColorByKey(theme, resolveColorKey(event))
}

/** True when the tile uses solid fill + white ink (a dark source). */
export function isSolidInk(event: CalendarEvent): boolean {
  return SOLID_INK_KEYS.has(resolveColorKey(event))
}
